import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CelebrationIntro.css';

const COLORS = ['#fbbf24', '#fb7185', '#34d399', '#60a5fa', '#c084fc', '#f97316'];

export default function CelebrationIntro() {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(5);
  const [phase, setPhase] = useState('countdown');

  const confetti = useMemo(() => Array.from({ length: 84 }, (_, index) => {
    const fromLeft = index % 2 === 0;
    const distance = 18 + ((index * 37) % 70);
    const rise = 34 + ((index * 29) % 56);
    return {
      id: index,
      className: index % 5 === 0 ? 'celebration-confetti celebration-confetti--round' : 'celebration-confetti',
      style: {
        '--start-x': fromLeft ? '11vw' : '89vw',
        '--burst-x': `${fromLeft ? distance : -distance}vw`,
        '--peak-y': `${-rise}vh`,
        '--fall-y': `${22 + ((index * 17) % 48)}vh`,
        '--spin': `${360 + ((index * 71) % 720)}deg`,
        '--delay': `${(index % 14) * 0.025}s`,
        '--duration': `${2.15 + (index % 7) * 0.13}s`,
        '--confetti-color': COLORS[index % COLORS.length],
      },
    };
  }), []);

  useEffect(() => {
    if (phase !== 'countdown') return undefined;
    const timer = window.setInterval(() => {
      setCountdown((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          setPhase('celebrating');
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'celebrating') return undefined;
    const leaveTimer = window.setTimeout(() => setPhase('leaving'), 3400);
    const finishTimer = window.setTimeout(() => setPhase('done'), 4000);
    return () => {
      window.clearTimeout(leaveTimer);
      window.clearTimeout(finishTimer);
    };
  }, [phase]);

  if (phase === 'done') return null;

  const isCelebrating = phase === 'celebrating' || phase === 'leaving';

  return (
    <div
      className={`celebration-intro ${isCelebrating ? 'celebration-intro--open' : ''} ${phase === 'leaving' ? 'celebration-intro--leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('Welcome celebration')}
    >
      <div className="celebration-intro__aurora" aria-hidden="true" />
      <button type="button" className="celebration-intro__skip" onClick={() => setPhase('done')}>
        {t('Skip')}
      </button>

      {!isCelebrating ? (
        <div className="celebration-countdown" aria-live="polite">
          <p className="celebration-countdown__eyebrow">{t('A surprise is waiting for you')}</p>
          <div key={countdown} className="celebration-countdown__ring">
            <span>{countdown}</span>
          </div>
          <p className="celebration-countdown__label">{t('Get ready...')}</p>
        </div>
      ) : (
        <div className="celebration-stage">
          <div className="celebration-confetti-field" aria-hidden="true">
            {confetti.map((piece) => <i key={piece.id} className={piece.className} style={piece.style} />)}
            <div className="celebration-cannon celebration-cannon--left"><span /></div>
            <div className="celebration-cannon celebration-cannon--right"><span /></div>
          </div>

          <div className="celebration-message">
            <span className="celebration-message__sparkle" aria-hidden="true">✦</span>
            <p>{t('Congratulations!')}</p>
            <h1>{t("Welcome to L'mcid")}</h1>
            <small>{t('Your learning journey starts here.')}</small>
          </div>

          <div className="celebration-gift" aria-hidden="true">
            <div className="celebration-gift__glow" />
            <div className="celebration-gift__lid">
              <span className="celebration-gift__lid-ribbon" />
            </div>
            <div className="celebration-gift__box">
              <span className="celebration-gift__ribbon" />
              <span className="celebration-gift__shine" />
            </div>
            <div className="celebration-gift__bow celebration-gift__bow--left" />
            <div className="celebration-gift__bow celebration-gift__bow--right" />
            <div className="celebration-gift__knot" />
          </div>
        </div>
      )}
    </div>
  );
}
